import { useCallback, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useApolloClient } from "@apollo/client"

import { useNavigationContext } from "shared/context/navigation/NavigationContext"
import { Logger } from "shared/lib/Logger"
import { reduceTree } from "shared/lib/treeUtils"
import { useEvent } from "shared/lib/useEvent"
import {
  Project,
  RoomTypename,
  isProjectFloor,
  isProjectProductPosition,
  isProjectRoom
} from "shared/types/Project"
import { DragSource } from "shared/types/drag-and-drop/DragSource"
import { DropTarget } from "shared/types/drag-and-drop/DropTarget"

import { useFloorFragment } from "entities/floors/model/useFloorFragment"
import { useProjectChildrenSorting } from "entities/project-children/api/useProjectChildrenSorting"
import { readProjectChildFragment } from "entities/project-children/lib/readProjectChildFragment"
import { useUpdateProject } from "entities/projects/api/useUpdateProject"
import { useProjectCollaboration } from "features/project-collaboration/model/useProjectCollaboration"
import { useProject } from "features/project-management/model/useProject"
import { useProjectInitialSelection } from "features/project-management/model/useProjectInitialSelection"
import { useProjectStore } from "features/project-management/model/useProjectStore"
import { ProjectFormTabType } from "features/project-management/types/ProjectFormTabType"
import { useProjectTeamScopedActionHandlers } from "features/project-team/model/useProjectTeamScopedActionHandlers"
import { EmptyStateHint } from "widgets/product-position-table/EmptyStateHint"
import { ProjectChatButton } from "widgets/project-chat-button/ProjectChatButton"

import { ProjectPageModel } from "../ProjectPage"
import { ProjectPageConnectorProps } from "../ProjectPageConnector"
import { ProjectPageSidebarFactory } from "../sidebar/ProjectPageSidebarFactory"
import { useOpenProjectChildren } from "../sidebar/hooks/useOpenProjectChildren"
import { useSelectedProjectChildren } from "../sidebar/hooks/useSelectedProjectChildren"
import { useFloorSort } from "./useFloorSort"
import { useProjectPageActionsButton } from "./useProjectPageActionsButton"
import { useProjectPageAdditionalActions } from "./useProjectPageAdditionalActions"
import { useProjectTabState } from "./useProjectTabState"

export function useProjectPageModel(
  args: ProjectPageConnectorProps
): ProjectPageModel {
  const { t } = useTranslation()
  const { projectID, page } = args
  const projectModel = useProject(
    { projectID },
    {
      includeChildren: true,
      includeAttachments: true,
      includeTeam: true
    }
  )
  const { updateProject } = useUpdateProject()
  const handleUpdate = useEvent((updates: Partial<Project>) => {
    updateProject(projectID, updates)
  })

  const {
    activeTabID: requestedTabID,
    selectedElementIDs,
    collapsedRoomIDs,
    expandedProductPositionIDs,
    pendingElementIDs,
    collapseRoom,
    expandProductPosition,
    collapseAll,
    expandAll,
    activateFloor,
    selectElement,
    unselectAll,
    openElementDetails,
    enterSidebarEditing,
    setSidebarActiveTab
  } = useProjectStore(projectID)

  const { cache } = useApolloClient()

  const isItemCollapsed = useCallback(
    (item: { id: string; __typename?: string }) => {
      if (item.__typename === RoomTypename) {
        return collapsedRoomIDs.includes(item.id)
      }
      return !expandedProductPositionIDs.includes(item.id)
    },
    [collapsedRoomIDs, expandedProductPositionIDs]
  )

  const onItemCollapse = useEvent((id: string) => {
    const child = readProjectChildFragment(id, cache)
    if (isProjectRoom(child)) collapseRoom(id)
    else if (isProjectProductPosition(child)) expandProductPosition(id)
    else {
      Logger.error(
        "InvalidDataError",
        "project element needs to be a room or product position",
        { data: child }
      )
    }
  })

  const onCollapseAll = useEvent(() => {
    const children = projectModel?.core.data.project?.data?.children ?? []
    const roomIDs = reduceTree(
      children,
      (acc: string[], item) => {
        if (isProjectRoom(item)) acc.push(item.id)
        return acc
      },
      []
    )
    collapseAll(roomIDs)
  })

  const onExpandAll = useEvent(() => {
    const children = projectModel?.core.data.project?.data?.children ?? []
    const ppIDs = reduceTree(
      children,
      (acc: string[], item) => {
        if (isProjectProductPosition(item)) acc.push(item.id)
        return acc
      },
      []
    )
    expandAll(ppIDs)
  })

  useProjectInitialSelection({ projectID })

  const { activeTabID, floors } = useProjectTabState({
    projectModel,
    requestedTabID
  })
  const { activeFloorID } = useProjectStore(projectID)

  useEffect(() => {
    if (requestedTabID !== activeTabID) {
      activateFloor(activeTabID)
    }
  }, [requestedTabID, activeTabID])

  const { move, insert } = useProjectChildrenSorting({
    projectID,
    floorID: activeFloorID
  })

  const { onManage: onManageProjectTeam } = useProjectTeamScopedActionHandlers({
    projectID
  })

  const selectedChildren = useSelectedProjectChildren({ projectID })
  const openChildren = useOpenProjectChildren({ projectID })

  const additionalActions = useProjectPageAdditionalActions({
    projectID,
    page
  })

  const onActions = useProjectPageActionsButton(projectID, {
    additionalActions
  })

  useProjectCollaboration({
    projectID,
    subscriberID: `project-page-${projectID}`
  })

  const { notify } = useNavigationContext()
  useEffect(() => {
    notify()
  }, [projectID])

  const actionsButtonLabel = useMemo(() => {
    if (isProjectRoom(selectedChildren[0])) {
      return t("Room.Actions.Actions")
    }
    if (isProjectFloor(selectedChildren[0])) {
      return t("Floor.Actions.Actions")
    }
    if (isProjectProductPosition(selectedChildren[0])) {
      return t("ProductPosition.Actions.Actions")
    }
    return t("Project.Actions.Actions")
  }, [selectedChildren, t])

  const handleFloorSort = useFloorSort({
    projectID,
    floors
  })

  const handleDirectDropIntoFloor = useEvent(
    (dragSource: DragSource, dropTarget: DropTarget) => {
      if (isProjectRoom(dragSource.data) && isProjectFloor(dropTarget.data)) {
        insert(dragSource.data?.id, 0, dropTarget.data.id)
      } else {
        Logger.error("ProjectDragAndDropError", "invalid drop", {
          data: { dragSource, dropTarget }
        })
      }
    }
  )

  const selectedFloor = useFloorFragment(activeTabID ?? "")

  const handleShowDetails = useEvent(() => {
    unselectAll()
    openElementDetails()
  })

  const handleEditInSidebar = useEvent(() => {
    unselectAll()
    enterSidebarEditing()
  })

  const handleAddClarificationRequest = useEvent(() => {
    unselectAll()
    enterSidebarEditing()
    setSidebarActiveTab(ProjectFormTabType.CLARIFICATION_REQUEST)
  })

  return {
    projectModel: {
      ...projectModel,
      core: {
        ...projectModel.core,
        handlers: {
          ...projectModel.core.handlers,
          onShowDetails: handleShowDetails,
          onEdit: projectModel.core.handlers.onEdit
            ? handleEditInSidebar
            : undefined,
          onAddClarificationRequest: projectModel.core.handlers
            .onAddClarificationRequest
            ? handleAddClarificationRequest
            : undefined
        }
      }
    },
    additionalActions,

    noDataHint: (
      <EmptyStateHint
        floor={selectedFloor}
        activeTabID={activeTabID}
        onDrop={move}
      />
    ),

    activeTabID,
    selectedElementIDs,
    isItemCollapsed,
    isSortingDisabled: projectModel.core.data.project?.isUpdating,
    pendingProjectChildren: pendingElementIDs,

    chatWidgets: null, // <ContentTag content={page} attribute="chatWidgets" />,
    chatButton: <ProjectChatButton page={page} />,

    onActions,
    onUpdate: handleUpdate,
    onItemCollapse,
    onCollapseAll,
    onExpandAll,
    onSelect: selectElement,
    onTabSelect: activateFloor,
    onManageProjectTeam,
    onPositionChange: move,
    onFloorSort: handleFloorSort,
    onDirectDropIntoFloor: handleDirectDropIntoFloor,

    actionsButtonLabel,

    sidebar: (
      <ProjectPageSidebarFactory
        projectID={projectID}
        selectedChildren={openChildren}
      />
    )
  }
}
